
//decoradores
import {
  Component,
  Emit,
  Prop,
  VModel,
  Vue,
  Watch,
} from "vue-property-decorator";

//helpers
import "@/helpers/extensions";
import { roundTo } from "@/helpers/number";

//google maps
import { gmapApi, google } from "vue2-google-maps";

//tipos
import { Empresa } from "@/typings/store/plugins/easyFirestore/empresas";
import { Barredor } from "@/typings/store/plugins/easyFirestore/barredores";
import { PingRastreador } from "@/typings/store/plugins/easyFirestore/pingsRastreador";
import {
  TiempoPings,
  RutasBarredor,
} from "@/typings/store/plugins/easyFirestore/rutasBarredor";
import {
  HorariosBarredores,
  Polyline,
  UltimaPosicion,
} from "@/typings/components/barredores/barredores";

//componentes
import RutaMapa from "@/components/barredores/mapa/Ruta.vue";

@Component({
  components: {
    RutaMapa,
  },
})
export default class MapaBarredores extends Vue {
  @VModel() tiempoPings!: TiempoPings;
  @Prop() readonly fecha!: string;
  @Prop() readonly empresa!: Empresa;
  @Prop() readonly rutas!: RutasBarredor;
  @Prop() readonly seleccionados!: Barredor[];
  @Prop() readonly barredores!: Array<Barredor>;
  @Prop() readonly items!: Array<PingRastreador>;

  created(): void {
    if (this.empresa.posicion) {
      this.center = this.empresa.posicion;
    }
  }

  hoy = new Date();
  zoom = 14;
  center = {
    lat: -35.529852,
    lng: -71.490037,
  };
  heatmap: google.maps.visualization.HeatmapLayer | null = null;
  showHeatmap = false;

  get google(): google {
    return gmapApi();
  }

  get titleHeatmap(): string {
    const index = this.showHeatmap ? 1 : 0;
    return this.$t(`barredores.mapa.heatmap.tooltip.${index}`) as string;
  }

  get historico(): boolean {
    const fechaSeleccionada = this.fecha.toDate();
    fechaSeleccionada.setHours(23, 59, 59, 999);
    return fechaSeleccionada < this.hoy;
  }

  get horarios(): HorariosBarredores {
    const regex = /^(2[0-3]|[0-1]\d):[0-5]\d-(2[0-3]|[0-1]\d):[0-5]\d$/;
    const result: HorariosBarredores = {};
    for (const barredor of this.barredores) {
      if (!barredor.id) continue;
      let [horaInicio, minutosInicio] = [0, 0];
      let [horaFin, minutosFin] = [23, 59];
      if (barredor.horario && regex.test(barredor.horario)) {
        const [horarioInicio, horarioFin] = barredor.horario.split("-");
        [horaInicio, minutosInicio] = horarioInicio.split(":").map(Number);
        [horaFin, minutosFin] = horarioFin.split(":").map(Number);
      }
      const fechaInicio = this.fecha.toDate();
      fechaInicio.setHours(horaInicio, minutosInicio, 0, 0);
      const fechaFin = this.fecha.toDate();
      fechaFin.setHours(horaFin, minutosFin, 59, 999);
      result[barredor.id] = {
        fechaInicio,
        fechaFin,
      };
    }
    return result;
  }

  get itemsOrdenados(): Array<PingRastreador> {
    const timestampCache = new Map<PingRastreador, number>();
    const getTimestamp = (ping: PingRastreador): number => {
      let ts = timestampCache.get(ping);
      if (ts === undefined) {
        ts = ping.fecha.toDate().getTime();
        timestampCache.set(ping, ts);
      }
      return ts;
    };
    const items = this.items.slice();
    items.sort((a, b) => getTimestamp(a) - getTimestamp(b));
    return items;
  }

  get agrupados(): { [id: string]: Array<PingRastreador> } {
    const result: { [id: string]: Array<PingRastreador> } = {};
    for (const ping of this.itemsOrdenados) {
      if (!ping.valido) continue;
      const fechaPing = ping.fecha.toDate();
      for (const barredor of ping.barredores) {
        if (!barredor.id) continue;
        const id = barredor.id;
        const horario = this.horarios[id];
        if (
          horario &&
          horario.fechaInicio <= fechaPing &&
          fechaPing <= horario.fechaFin
        ) {
          if (!result[id]) {
            result[id] = [];
          }
          result[id].push(ping);
        }
      }
    }
    return result;
  }

  get heatmapData(): Array<google.maps.visualization.WeightedLocation> {
    const result: Array<google.maps.visualization.WeightedLocation> = [];
    if (!this.google) return result;
    for (const id in this.agrupados) {
      this.agrupados[id].forEach((barredor, index, ordenados) => {
        if (ordenados.length - 1 == index) return;
        const barredor2 = ordenados[index + 1];
        const inicioLat = roundTo(barredor.posicion.lat, 4);
        const inicioLng = roundTo(barredor.posicion.lng, 4);
        const finLat = roundTo(barredor2.posicion.lat, 4);
        const finLng = roundTo(barredor2.posicion.lng, 4);
        const latDiff = finLat - inicioLat;
        const lngDiff = finLng - inicioLng;
        const latSign = Math.sign(latDiff);
        const lngSign = Math.sign(lngDiff);
        const latDiffAbs = Math.abs(latDiff);
        const lngDiffAbs = Math.abs(lngDiff);
        if (latDiffAbs > lngDiffAbs) {
          for (let i = 0; i < latDiffAbs; i += 0.0001) {
            const lat = inicioLat + i * latSign;
            const lng = inicioLng + (lngDiff / latDiffAbs) * i;
            result.push({
              location: new this.google.maps.LatLng(lat, lng),
              weight: 8,
            });
          }
        } else {
          for (let i = 0; i < lngDiffAbs; i += 0.0001) {
            const lat = inicioLat + (latDiff / lngDiffAbs) * i;
            const lng = inicioLng + i * lngSign;
            result.push({
              location: new this.google.maps.LatLng(lat, lng),
              weight: 8,
            });
          }
        }
      });
    }
    return result;
  }

  get polylinesRutasPlanificadas(): Array<Polyline> {
    const polylines: Array<Polyline> = [];
    for (const barredor of this.barredores) {
      if (!barredor.id) continue;
      if (
        this.seleccionados.some(
          (seleccionado) => seleccionado.id === barredor.id,
        )
      ) {
        const ruta = barredor.ruta?.id ? this.rutas[barredor.ruta.id] : null;
        if (ruta && barredor.mostrarRuta) {
          polylines.push({
            id: barredor.id,
            path: ruta.posiciones,
            color: barredor.color ?? "#FF0000",
          });
        }
      }
    }
    return polylines;
  }

  get map(): Vue & { $mapObject: google.maps.Map } {
    return this.$refs.map as Vue & { $mapObject: google.maps.Map };
  }

  @Emit("change:seleccionado")
  onChangeSeleccionado(barredor: Barredor): Barredor {
    return barredor;
  }

  @Watch("heatmapData")
  onHeatmapDataChanged(): void {
    if (this.heatmap) {
      this.heatmap.setData(this.heatmapData);
    }
  }

  mostrarHeatmap(): void {
    this.showHeatmap = !this.showHeatmap;
    if (this.showHeatmap) {
      this.heatmap = new this.google.maps.visualization.HeatmapLayer({
        data: this.heatmapData,
        map: this.map.$mapObject,
        radius: 10,
        opacity: 0.6,
        maxIntensity: 100,
      });
    } else if (this.heatmap) {
      this.heatmap.setMap(null);
    }
  }

  updateUltimaPosicion(ultimaPosicion: UltimaPosicion): void {
    if (!ultimaPosicion.barredor.id) return;
    const ultimoPing = ultimaPosicion.ping;
    const fecha = ultimoPing?.fecha?.toDate() ?? new Date();
    if (!ultimoPing) fecha.setHours(0, 0, 0, 0);
    const diferencia = new Date().getTime() - fecha.getTime();
    this.$set(this.tiempoPings, ultimaPosicion.barredor.id, diferencia);
  }

  centrarMapa(): void {
    const bounds = new this.google.maps.LatLngBounds();
    for (const seleccionado of this.seleccionados) {
      if (!seleccionado?.id) continue;
      const agrupados = this.agrupados[seleccionado.id];
      if (!agrupados) continue;
      const posiciones = agrupados.map((agrupado) => agrupado.posicion);
      for (const posicion of posiciones) {
        bounds.extend(posicion);
      }
    }
    if (!bounds.isEmpty()) this.map.$mapObject.fitBounds(bounds);
  }

  boundsChangedHandler(): void {
    const zoom = this.map.$mapObject.getZoom();
    if (!zoom) return;
    this.zoom = zoom;
  }

  seleccionar(id: string): void {
    const barredor = this.barredores.find((barredor) => barredor.id === id);
    if (barredor) this.onChangeSeleccionado(barredor);
  }
}
